import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { CheckCircle2, Loader2, LogOut, ScanLine } from 'lucide-react';
import { ImageWrapper } from './ImageWrapper.jsx';
import { LoyaltyBadge } from './LoyaltyBadge.jsx';
import { LoyaltyStampSheet } from './LoyaltyStampSheet.jsx';
import { fs } from '../utils/a11y.js';

const FIELD_INPUT_TYPES = {
  phone: 'tel',
  email: 'email',
  password: 'password',
  // Barcode/QR scanners act as a keyboard: they "type" the decoded value into
  // whatever input is focused, then send Enter. A plain text input already
  // handles that (and Enter already submits the <form>) — this type just adds
  // auto-focus and a visual hint so the shopper knows they can scan here.
  scan: 'text',
};

export function IdentificationModal({
  open,
  onClose,
  programs,
  identifiedPrograms,
  onIdentify,
  onLogout,
  logActivity,
  drawerContainer,
}) {
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedProgramId(null);
      setFieldValues({});
      setError(null);
      return;
    }
    if (programs && programs.length === 1) {
      setSelectedProgramId(programs[0].id);
    }
  }, [open, programs]);

  if (!open) return null;

  const selectedProgram = (programs || []).find((p) => p.id === selectedProgramId);
  const selectedMembership = selectedProgramId ? identifiedPrograms?.[selectedProgramId] : null;

  // Tier and stamps are stored on the membership as bare ids, so the readable names come
  // from the program's own config.
  const membership = selectedMembership?.membership;
  const memberName = membership?.status_snapshot?.name || null;
  const stampCategories = selectedProgram?.stamp_categories || [];

  // A member only gets a tier_id once a purchase has been through the rules, so until
  // then they are simply at the entry level — the lowest one the program defines.
  // Without this the shopper sees no tier at all, which reads as "you have none".
  const sortedTiers = [...(selectedProgram?.tiers || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const tier = sortedTiers.find((t) => t.id === membership?.tier_id) || sortedTiers[0] || null;

  const handleSelectProgram = (programId) => {
    logActivity('identification-modal', 'select-program', 'click', true, { program_id: programId });
    setFieldValues({});
    setError(null);
    setSelectedProgramId(programId);
  };

  const handleFieldChange = (fieldId, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Leaving drops the identification for the whole sale, so any member-only promotion
  // disappears from the cart along with it.
  const handleLogout = () => {
    logActivity('identification-modal', 'logout-button', 'click', true, { program_id: selectedProgramId });
    onLogout();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const missingRequired = (selectedProgram.identifier_fields || []).some(
      (field) => field.validation?.required && !fieldValues[field.id]
    );
    if (missingRequired) {
      setError('Completa todos los campos requeridos.');
      return;
    }

    setSubmitting(true);
    logActivity('identification-modal', 'submit-button', 'click', true, { program_id: selectedProgramId });
    try {
      const result = await onIdentify(selectedProgramId, fieldValues);
      if (!result.success) {
        setError(result.message || 'No pudimos identificarte, revisa tus datos.');
      } else {
        onClose();
      }
    } catch (err) {
      setError('Ocurrió un error al identificarte. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        aria-describedby="Identification Modal"
        description="Identification Modal"
        container={drawerContainer}
        className="bg-mid shadow-2xl p-8 w-full max-w-[560px] rounded-[24px] border-[4px]"
      >
        {/* DialogTitle already renders the heading element, so the styles go on it
            directly - nesting one inside it is invalid HTML. */}
        <DialogTitle className="p-0 w-full secondary-font-bold text-center" style={{ fontSize: fs(30), lineHeight: fs(30) }}>
          {selectedMembership ? 'Mi cuenta' : 'Identifícate'}
        </DialogTitle>

        <div className="w-full mt-6">
          {!selectedProgramId ? (
            <div className="flex flex-col gap-3">
              <p className="text-center text-(--color-text-subtle) mb-2">Elige tu programa de fidelización</p>
              <div className="flex flex-row flex-wrap justify-center gap-4">
                {(programs || []).map((program) => {
                  const identified = !!identifiedPrograms?.[program.id];
                  return (
                    <button
                      key={program.id}
                      onClick={() => handleSelectProgram(program.id)}
                      className="cursor-pointer flex flex-col items-center gap-2 w-[96px]"
                    >
                      <div className="relative w-[72px] h-[72px]">
                        <div
                          className={`w-full h-full rounded-full border-[2px] bg-mid overflow-hidden flex items-center justify-center transition-colors hover:border-(--color-text-subtle) ${
                            identified ? 'border-green-600' : 'border-[#E1E1E1]'
                          }`}
                        >
                          {program.logo ? (
                            <ImageWrapper
                              image={program.logo}
                              alt={program.name}
                              display="block"
                              width="100%"
                              height="100%"
                              backgroundColor="transparent"
                              margin="0"
                              padding="0"
                              borderRadius="0"
                              transition={true}
                              loadDelay={0.1}
                            />
                          ) : (
                            <span className="secondary-font-bold text-xl uppercase">
                              {(program.name || '?').slice(0, 2)}
                            </span>
                          )}
                        </div>
                        {identified && (
                          <div className="absolute -top-1 -right-1 bg-mid rounded-full">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          </div>
                        )}
                      </div>
                      <span className="secondary-font-bold text-sm text-center line-clamp-2">{program.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : selectedMembership ? (
            <div className="flex flex-col items-center gap-4">
              <CheckCircle2 className="w-12 h-12 text-green-600" />

              <div className="flex flex-col items-center gap-1">
                <p className="text-center secondary-font-bold fs-24 lh-28">
                  {memberName ? `Hola, ${memberName}` : 'Ya estás identificado'}
                </p>
                <p className="text-center text-(--color-text-subtle)">{selectedProgram?.name}</p>
              </div>

              {/* Only shown when the program actually defines them: a program with no
                  tiers or no stamp categories would otherwise show empty sections. */}
              {tier && (
                <div className="flex flex-col items-center gap-1">
                  <span className="text-(--color-text-subtle) fs-14">Tu nivel</span>
                  <div className="flex items-center gap-2">
                    <LoyaltyBadge image={tier.image_url} emoji={tier.emoji} name={tier.name} size={40} />
                    <span className="secondary-font-bold fs-20">{tier.name}</span>
                  </div>
                </div>
              )}

              {stampCategories.length > 0 && (
                <div className="w-full flex flex-col items-center gap-2">
                  <span className="text-(--color-text-subtle) fs-14">Tus sellos</span>
                  <div className="flex flex-row flex-wrap justify-center gap-6">
                    {stampCategories.map((category) => (
                      <LoyaltyStampSheet
                        key={category.id}
                        category={category}
                        count={membership?.stamps?.[category.id] || 0}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="w-full flex flex-col items-center gap-3 mt-2">
                {(programs || []).length > 1 && (
                  <button
                    onClick={() => setSelectedProgramId(null)}
                    className="text-(--color-text-subtle) underline"
                  >
                    Elegir otro programa
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="cursor-pointer w-full h-[60px] rounded-[40px] border-[2px] border-[#E1E1E1] secondary-font-bold flex items-center justify-center gap-2"
                >
                  <LogOut className="w-5 h-5" />
                  No soy yo
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {(programs || []).length > 1 && (
                <button
                  type="button"
                  onClick={() => setSelectedProgramId(null)}
                  className="text-(--color-text-subtle) underline self-start"
                >
                  Elegir otro programa
                </button>
              )}
              {(selectedProgram?.identifier_fields || []).map((field) => (
                <div key={field.id} className="flex flex-col gap-1">
                  <label className="secondary-font-bold fs-16">{field.label}</label>
                  <div className="relative">
                    <input
                      type={FIELD_INPUT_TYPES[field.type] || 'text'}
                      required={!!field.validation?.required}
                      value={fieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      autoFocus={field.type === 'scan'}
                      autoComplete="off"
                      className={`h-[56px] px-[20px] rounded-[16px] border-[2px] border-[#E1E1E1] outline-none w-full ${field.type === 'scan' ? 'pr-[52px]' : ''}`}
                      placeholder={field.label}
                    />
                    {field.type === 'scan' && (
                      <ScanLine className="absolute right-[16px] top-1/2 -translate-y-1/2 w-5 h-5 text-(--color-text-subtle) pointer-events-none" />
                    )}
                  </div>
                </div>
              ))}

              {error && <p className="text-[#e70a2a] text-center">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="cursor-pointer h-[60px] rounded-[40px] bg-black text-white secondary-font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                Identificarme
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
